import type { ChannelId } from '@mixwire/contracts';
import type { ReactElement } from 'react';

export type Flagged = {
  readonly playedBefore: boolean;
  readonly clashChannelId: ChannelId | null;
  readonly playingNowChannelId: ChannelId | null;
};

// One badge, not three: what is on a deck right now beats a queued clash, which beats a repeat play.
const trackFlag = (item: Flagged): 'playing' | 'clash' | 'played' | 'none' => {
  if (item.playingNowChannelId !== null) {
    return 'playing';
  }
  if (item.clashChannelId !== null) {
    return 'clash';
  }
  if (item.playedBefore) {
    return 'played';
  }
  return 'none';
};

/**
 * Whose deck it is only means something to a DJ. A suggester holds no channel, so for them every
 * deck is somebody else's and the labels say so plainly rather than naming a "your" they do not have.
 */
const playingLabelFor = (item: Flagged, myChannelId: ChannelId | undefined): string => {
  if (myChannelId === undefined) {
    return 'Playing right now on this party';
  }
  return item.playingNowChannelId === myChannelId
    ? 'Playing on your deck right now'
    : 'Playing right now on another channel';
};

const clashLabelFor = (
  item: Flagged,
  myChannelId: ChannelId | undefined,
  clashLabel: string,
): string => {
  if (myChannelId === undefined) {
    return 'Already on a DJ’s queue';
  }
  return item.clashChannelId === myChannelId ? 'Already on your queue' : clashLabel;
};

/**
 * Every label distinguishes this DJ's own deck from somebody else's, because a warning that says
 * "another channel" at a party with one DJ names nobody and reads as a bug. `clashLabel` is the
 * only one the caller supplies: the lists mean different things by a clash — a suggestion or a
 * search result is not queued anywhere yet, while a track already on this queue is being reached
 * first by a rival deck.
 */
export const TrackFlagNote = ({
  item,
  myChannelId,
  clashLabel = 'Already queued on another channel',
}: {
  readonly item: Flagged;
  /** Absent on the suggester, which is looking at the party rather than holding a piece of it. */
  readonly myChannelId?: ChannelId | undefined;
  readonly clashLabel?: string | undefined;
}): ReactElement | null => {
  const flag = trackFlag(item);
  if (flag === 'none') {
    return null;
  }

  const tone =
    flag === 'played'
      ? { box: 'bg-warning-subtle', dot: 'bg-warning', label: 'Already played earlier this party' }
      : {
          box: 'bg-danger-subtle',
          dot: 'bg-danger',
          label:
            flag === 'playing'
              ? playingLabelFor(item, myChannelId)
              : clashLabelFor(item, myChannelId, clashLabel),
        };

  return (
    <div className={`${tone.box} rounded-control mt-2.5 flex items-center gap-2 px-2.5 py-1.5`}>
      <span className={`${tone.dot} size-1.5 shrink-0 rounded-full`} />
      <span className="text-content text-xs">{tone.label}</span>
    </div>
  );
};
